/**
 * 配送依頼一覧の列レイアウト（列の並び・幅・表示/非表示・固定）の保存値と、列定義とのマージ（#3690 VOC_PJ-577）。
 *
 * 保存値はテナント・ユーザー・画面ごとに 1 つ（拠点ごとには持たない。確定版 §2.4）。拠点ごとに出るカスタムキー列は
 * スロット（customKey1〜5）の key で識別するので、拠点 A の customKey1 の設定は拠点 B の customKey1 にも当たる。
 *
 * 保存値に「今の列定義に無い key」が入っていても捨てない。拠点を切り替えてカスタムキー列が消えた・
 * 将来の版で列を減らした、のどちらでも、元の拠点・版に戻ったときに設定を復元できるようにするため。
 * 書き戻すときも、今は無い key を元の位置関係のまま残す（{@link mergeColumnOrder}）。
 */

/** 保存値の形式の版。形式を変えたら上げ、{@link normalizeColumnLayout} で旧版を読み替える */
export const COLUMN_LAYOUT_VERSION = 1

/** 列幅の下限・上限（px）。つまみで極端に縮めて列が見えなくなる・広げすぎて横スクロールが伸び切るのを防ぐ */
export const COLUMN_WIDTH_MIN = 60
export const COLUMN_WIDTH_MAX = 800

export type ColumnLayout = {
  version: typeof COLUMN_LAYOUT_VERSION
  /** 列の並び（左から）。今の列定義に無い key も保持する */
  order: string[]
  /** 列幅を変えた列だけ持つ（無い列は列定義の既定幅） */
  widths: Record<string, number>
  hiddenKeys: string[]
  /**
   * 左に固定する列。null は「固定を一度も変えていない」で、列定義の既定の固定を使う。
   * 空配列とは区別する（固定は常に 1 列以上あるので、空配列が保存されることはない）
   */
  fixedKeys: string[] | null
  /**
   * 並べ替えていない（order が空）ときに保存する、その時点で見えていた列。ここに無い列は「新しい列」として
   * 末尾に足す（確定版 §2.4）。並びそのものは持たないので、既知の列は列定義の順のまま（既定の並びの変更が届く）。
   * 一度も保存していなければ無い（すべての列を既知として列定義の順に並べる）
   */
  knownKeys?: string[]
}

export const EMPTY_COLUMN_LAYOUT: ColumnLayout = {
  version: COLUMN_LAYOUT_VERSION,
  order: [],
  widths: {},
  hiddenKeys: [],
  fixedKeys: null
}

/** 列定義のうち、レイアウトのマージに要る情報 */
export type ColumnLayoutDefinition = {
  key: string
  defaultWidth: number
  isFixedByDefault: boolean
  /** false の列は表示項目設定で OFF にできない（ステータス・配送依頼No。操作の入口のため） */
  canHide: boolean
  /**
   * true の列は常に固定し、動かせず、固定からも外せない（確定版の「固定必須列」）。
   * どの列を当てるかは #3690 Q-7 の回答で決まる
   */
  isLocked: boolean
}

export type ResolvedColumn = {
  key: string
  width: number
  isHidden: boolean
  isFixed: boolean
  isLocked: boolean
}

const keysOf = (columns: { key: string }[]): string[] =>
  columns.map(({ key }) => key)

/**
 * 列を置ける最も左の位置。先頭が固定必須列なら、その右から（7-A: 固定必須列は左端から押し出されない）。
 * 固定必須列どうしのあいだ（今のまとめキーの位置）には置ける
 */
const firstMovableIndex = (columns: ResolvedColumn[]): number =>
  columns[0]?.isLocked ? 1 : 0

/** 表示している固定列の幅の合計 */
export const visibleFixedWidth = (columns: ResolvedColumn[]): number =>
  columns
    .filter((column) => column.isFixed && !column.isHidden)
    .reduce((total, { width }) => total + width, 0)

/**
 * 固定列の幅の上限。固定列の合計が表示幅を超えると、固定しない列も固定の境界も画面外に出て、
 * 見出しのドラッグで固定を外せなくなるため、合計を表示幅より小さく保つ
 */
export type FixedWidthLimit = { maxFixedWidth?: number }

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

const clampWidth = (width: number): number =>
  Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(width)))

/**
 * localStorage から読んだ値を {@link ColumnLayout} に直す。
 * 壊れた値（手で書き換えた、別の形式の値が残っている）は項目単位で捨て、読める項目だけ活かす。
 * 版が違う・オブジェクトでない値は丸ごと既定に戻す。
 */
export const normalizeColumnLayout = (value: unknown): ColumnLayout => {
  if (typeof value !== 'object' || value === null) return EMPTY_COLUMN_LAYOUT
  const raw = value as Record<string, unknown>
  if (raw.version !== COLUMN_LAYOUT_VERSION) return EMPTY_COLUMN_LAYOUT

  const order = isStringArray(raw.order) ? [...new Set(raw.order)] : []

  const widths: Record<string, number> = {}
  if (typeof raw.widths === 'object' && raw.widths !== null) {
    for (const [key, width] of Object.entries(raw.widths)) {
      if (typeof width === 'number' && Number.isFinite(width)) {
        widths[key] = clampWidth(width)
      }
    }
  }

  const hiddenKeys = isStringArray(raw.hiddenKeys)
    ? [...new Set(raw.hiddenKeys)]
    : []
  const fixedKeys =
    isStringArray(raw.fixedKeys) && raw.fixedKeys.length > 0
      ? [...new Set(raw.fixedKeys)]
      : null

  return {
    version: COLUMN_LAYOUT_VERSION,
    order,
    widths,
    hiddenKeys,
    fixedKeys,
    ...(isStringArray(raw.knownKeys)
      ? { knownKeys: [...new Set(raw.knownKeys)] }
      : {})
  }
}

/**
 * 列定義の並びに保存値の並びを当てる。
 * 保存値にある列はその順に並べ、保存値に無い列（新しく現れたカスタムキー列、将来足した列）は
 * 列定義の順で末尾に足す（確定版 §2.4「移動可能列の末尾へ追加」）。保存値が空なら列定義の順になる。
 */
const orderKeys = (
  definitions: ColumnLayoutDefinition[],
  savedOrder: string[],
  knownKeys?: string[]
): string[] => {
  const definedKeys = new Set(definitions.map(({ key }) => key))
  if (savedOrder.length === 0 && knownKeys) {
    // 並べ替えていない: 既知の列は列定義の順、見たことのない列は末尾
    const known = new Set(knownKeys)
    return [
      ...definitions.map(({ key }) => key).filter((key) => known.has(key)),
      ...definitions.map(({ key }) => key).filter((key) => !known.has(key))
    ]
  }
  const ordered = savedOrder.filter((key) => definedKeys.has(key))
  const placed = new Set(ordered)
  return [
    ...ordered,
    ...definitions.map(({ key }) => key).filter((key) => !placed.has(key))
  ]
}

/**
 * 保存値と列定義から、今の画面に出す列の並び・幅・表示/非表示・固定を決める。
 *
 * 守る形:
 * - 固定列は左にひと続き（固定の列を左へ寄せる。固定の列どうしの並びは保つ）。
 *   rc-table は固定列の間に非固定列が挟まると列が分離して描かれる
 * - 表示している固定列が 1 列以上ある（0 列だと見出しのドラッグで固定へ落とす先がなくなる）。
 *   保存値で全部外れていたら、表示している先頭の列を固定する
 * - canHide=false の列は非表示にしない
 * - isLocked の列は常に固定する
 */
export const resolveColumnLayout = (
  definitions: ColumnLayoutDefinition[],
  layout: ColumnLayout
): ResolvedColumn[] => {
  const definitionByKey = new Map(definitions.map((d) => [d.key, d]))
  const hidden = new Set(layout.hiddenKeys)
  const fixed = new Set(
    layout.fixedKeys ??
      definitions.filter((d) => d.isFixedByDefault).map(({ key }) => key)
  )

  const columns = orderKeys(definitions, layout.order, layout.knownKeys).map(
    (key) => {
      const definition = definitionByKey.get(key)!
      return {
        key,
        width: layout.widths[key] ?? definition.defaultWidth,
        isHidden: definition.canHide && hidden.has(key),
        isFixed: definition.isLocked || fixed.has(key),
        isLocked: definition.isLocked
      }
    }
  )

  if (!columns.some((column) => column.isFixed && !column.isHidden)) {
    const first = columns.find((column) => !column.isHidden)
    if (first) first.isFixed = true
  }

  return [
    ...columns.filter((column) => column.isFixed),
    ...columns.filter((column) => !column.isFixed)
  ]
}

/**
 * 画面上の新しい並び（今の列定義にある key だけ）を、保存値の並びに書き戻す。
 * 保存値にあって今の列定義に無い key は、保存値の中で直前にあった key の後ろに残す。
 */
export const mergeColumnOrder = (
  savedOrder: string[],
  nextVisibleOrder: string[]
): string[] => {
  const visible = new Set(nextVisibleOrder)
  const merged = [...nextVisibleOrder]
  savedOrder.forEach((key, index) => {
    if (visible.has(key) || merged.includes(key)) return
    const predecessor = savedOrder
      .slice(0, index)
      .reverse()
      .find((candidate) => merged.includes(candidate))
    const insertAt = predecessor ? merged.indexOf(predecessor) + 1 : 0
    merged.splice(insertAt, 0, key)
  })
  return merged
}

/**
 * 画面上で決めた列の状態（並び・幅・表示/非表示・固定）を、保存値に書き戻す。
 * - 今の列定義に無い key の設定（他の拠点のカスタムキー列など）は消さない
 * - 幅は既定と違う列だけ、固定は既定と違うときだけ持つ。既定どおりの値まで保存すると、
 *   将来の版で既定の幅・固定を変えても、一度でも設定を触った利用者には届かなくなるため
 */
export const toColumnLayout = (
  previous: ColumnLayout,
  columns: ResolvedColumn[],
  definitions: ColumnLayoutDefinition[]
): ColumnLayout => {
  const currentKeys = new Set(columns.map(({ key }) => key))
  const keepOthers = (keys: string[]) =>
    keys.filter((key) => !currentKeys.has(key))
  const definitionByKey = new Map(definitions.map((d) => [d.key, d]))

  const widths: Record<string, number> = Object.fromEntries(
    Object.entries(previous.widths).filter(([key]) => !currentKeys.has(key))
  )
  columns.forEach(({ key, width }) => {
    if (width !== definitionByKey.get(key)?.defaultWidth) {
      widths[key] = clampWidth(width)
    }
  })

  const fixedNow = columns.filter((c) => c.isFixed).map(({ key }) => key)
  const othersFixed = keepOthers(previous.fixedKeys ?? [])
  const isDefaultFixed =
    othersFixed.length === 0 &&
    fixedNow.length ===
      columns.filter((c) => definitionByKey.get(c.key)?.isFixedByDefault)
        .length &&
    fixedNow.every((key) => definitionByKey.get(key)?.isFixedByDefault)

  const fixedKeys = isDefaultFixed ? null : [...othersFixed, ...fixedNow]
  const keys = columns.map(({ key }) => key)
  // 並べ替えていない間は並びを保存しない。列幅や表示を変えただけで全列の並びを持つと、
  // 将来の版で既定の並びを変えても、その利用者には届かなくなるため
  const isUnordered =
    previous.order.length === 0 &&
    keysOf(
      resolveColumnLayout(definitions, {
        ...EMPTY_COLUMN_LAYOUT,
        fixedKeys,
        knownKeys: previous.knownKeys
      })
    ).join('\n') === keys.join('\n')

  return {
    version: COLUMN_LAYOUT_VERSION,
    order: isUnordered ? [] : mergeColumnOrder(previous.order, keys),
    // 今の列定義に無い既知の列（他拠点のカスタムキー列）も残す
    ...(isUnordered
      ? { knownKeys: mergeColumnOrder(previous.knownKeys ?? [], keys) }
      : {}),
    widths,
    hiddenKeys: [
      ...keepOthers(previous.hiddenKeys),
      ...columns.filter((c) => c.isHidden).map(({ key }) => key)
    ],
    fixedKeys
  }
}

/** 列幅を変える（下限・上限に丸める） */
export const resizeColumn = (
  columns: ResolvedColumn[],
  key: string,
  width: number,
  { maxFixedWidth }: FixedWidthLimit = {}
): ResolvedColumn[] =>
  columns.map((column) => {
    if (column.key !== key) return column
    const isLimited =
      maxFixedWidth !== undefined && column.isFixed && !column.isHidden
    const limit = isLimited
      ? maxFixedWidth - (visibleFixedWidth(columns) - column.width)
      : Infinity
    return {
      ...column,
      width: Math.max(COLUMN_WIDTH_MIN, Math.min(clampWidth(width), limit))
    }
  })

/**
 * 表示/非表示を切り替えられるか。
 * canHide=false の列と、表示している最後の固定列は非表示にできない（固定が 0 列になるため）
 */
export const canToggleColumnVisibility = (
  columns: ResolvedColumn[],
  definitions: ColumnLayoutDefinition[],
  key: string
): boolean => {
  const definition = definitions.find((d) => d.key === key)
  if (!definition?.canHide) return false
  const target = columns.find((column) => column.key === key)
  if (!target) return false
  if (target.isHidden) return true
  const visibleFixed = columns.filter((c) => c.isFixed && !c.isHidden)
  return !(target.isFixed && visibleFixed.length === 1)
}

export const setColumnHidden = (
  columns: ResolvedColumn[],
  definitions: ColumnLayoutDefinition[],
  key: string,
  isHidden: boolean
): ResolvedColumn[] => {
  if (!canToggleColumnVisibility(columns, definitions, key)) return columns
  return columns.map((column) =>
    column.key === key ? { ...column, isHidden } : column
  )
}

/**
 * 列を from の位置から to の位置へ動かす（固定の範囲はまたがない）。
 * 固定と非固定をまたぐ移動は無視する。固定の出し入れは別の操作で行う
 */
export const moveColumnWithinGroup = (
  columns: ResolvedColumn[],
  fromKey: string,
  toKey: string
): ResolvedColumn[] => {
  const from = columns.findIndex(({ key }) => key === fromKey)
  const to = columns.findIndex(({ key }) => key === toKey)
  if (from < 0 || to < 0 || from === to) return columns
  if (columns[from].isLocked) return columns
  if (columns[from].isFixed !== columns[to].isFixed) return columns
  if (to < firstMovableIndex(columns)) return columns
  const next = [...columns]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** 表示している固定列が 1 列以上あるか（0 列になる操作は受け付けない） */
const hasVisibleFixedColumn = (columns: ResolvedColumn[]): boolean =>
  columns.some((column) => column.isFixed && !column.isHidden)

/**
 * 列を動かし、固定するかどうかも決める（固定への出し入れを伴う並べ替え）。
 * - beforeKey の列の直前に置く。null なら移動先の範囲（固定 / 固定しない）の末尾に置く
 * - 置いたあとも固定列は左にひと続きになる（beforeKey が移動先の範囲に無ければ範囲の末尾）
 * - 表示している固定列が 0 列になる移動は無視して元の列を返す（最後の 1 列は固定から外せない）
 * - isLocked の列は動かさない（元の列を返す）
 */
export const moveColumnTo = (
  columns: ResolvedColumn[],
  key: string,
  target: { isFixed: boolean; beforeKey: string | null },
  { maxFixedWidth }: FixedWidthLimit = {}
): ResolvedColumn[] => {
  const moving = columns.find((column) => column.key === key)
  if (!moving || moving.isLocked) return columns
  const rest = columns.filter((column) => column.key !== key)
  const fixed = rest.filter((column) => column.isFixed)
  const scrollable = rest.filter((column) => !column.isFixed)
  const group = target.isFixed ? fixed : scrollable
  const beforeIndex =
    target.beforeKey === null
      ? -1
      : group.findIndex((column) => column.key === target.beforeKey)
  const insertAt = beforeIndex < 0 ? group.length : beforeIndex
  group.splice(
    target.isFixed ? Math.max(insertAt, firstMovableIndex(fixed)) : insertAt,
    0,
    { ...moving, isFixed: target.isFixed }
  )
  const next = [...fixed, ...scrollable]
  if (!hasVisibleFixedColumn(next)) return columns
  if (
    maxFixedWidth !== undefined &&
    !moving.isFixed &&
    target.isFixed &&
    visibleFixedWidth(next) > maxFixedWidth
  ) {
    return columns
  }
  return next
}

/**
 * 固定列の合計が上限を超えるときは、右側の固定列から順に固定を外して表示する（固定必須列は外さない）。
 * 広い画面で保存したレイアウトを狭い画面で開いたときに、固定を外せなくならないようにするため。
 * 保存値は書き換えない（広い画面に戻れば保存した固定で出る）
 */
export const fitFixedColumns = (
  columns: ResolvedColumn[],
  maxFixedWidth: number
): ResolvedColumn[] => {
  if (visibleFixedWidth(columns) <= maxFixedWidth) return columns
  const fixed = columns.filter((column) => column.isFixed)
  const scrollable = columns.filter((column) => !column.isFixed)
  const released: ResolvedColumn[] = []
  while (
    visibleFixedWidth(fixed) > maxFixedWidth &&
    fixed.some((column) => !column.isLocked && !column.isHidden)
  ) {
    const index =
      fixed.length -
      1 -
      [...fixed]
        .reverse()
        .findIndex((column) => !column.isLocked && !column.isHidden)
    const [column] = fixed.splice(index, 1)
    released.unshift({ ...column, isFixed: false })
  }
  // 固定必須列だけで上限を超えるときは、表示の上だけ必須列の幅を比で縮める（下限は守る）
  const lockedWidth = visibleFixedWidth(fixed)
  const shrunk =
    lockedWidth > maxFixedWidth
      ? fixed.map((column) =>
          column.isHidden
            ? column
            : {
                ...column,
                width: Math.max(
                  COLUMN_WIDTH_MIN,
                  Math.floor((column.width * maxFixedWidth) / lockedWidth)
                )
              }
        )
      : fixed
  return [...shrunk, ...released, ...scrollable]
}

/** 固定を外したときに表示している固定列が 0 列になるか（最後の 1 列の保護の表示判定用） */
export const isLastVisibleFixedColumn = (
  columns: ResolvedColumn[],
  key: string
): boolean => {
  const target = columns.find((column) => column.key === key)
  if (!target?.isFixed || target.isHidden) return false
  return columns.filter((c) => c.isFixed && !c.isHidden).length === 1
}

/**
 * 固定する / 固定を外す。固定すると固定の範囲の末尾へ、外すと固定しない範囲の先頭へ移る
 * （画面上の位置がなるべく変わらないようにするため、境界のすぐ隣に置く）
 */
export const setColumnFixed = (
  columns: ResolvedColumn[],
  key: string,
  isFixed: boolean
): ResolvedColumn[] => {
  const firstScrollable = columns.find(
    (column) => !column.isFixed && column.key !== key
  )
  return moveColumnTo(columns, key, {
    isFixed,
    beforeKey: isFixed ? null : (firstScrollable?.key ?? null)
  })
}

/**
 * 表示項目設定の下書きを、開いている間に変わった列（拠点のカスタムキー設定の取得完了など）に合わせる。
 * 下書きの並び・表示/非表示は保ち、増えた列は各範囲（固定 / 固定しない）の末尾に足し、無くなった列は外す。
 * 列の集合が変わらなければ下書きをそのまま返す（未保存の変更を消さない）
 */
export const syncColumnsDraft = (
  draft: ResolvedColumn[],
  latest: ResolvedColumn[]
): ResolvedColumn[] => {
  const draftKeys = new Set(keysOf(draft))
  const latestKeys = new Set(keysOf(latest))
  const added = latest.filter(({ key }) => !draftKeys.has(key))
  const kept = draft.filter(({ key }) => latestKeys.has(key))
  if (added.length === 0 && kept.length === draft.length) return draft
  return [
    ...kept.filter((column) => column.isFixed),
    ...added.filter((column) => column.isFixed),
    ...kept.filter((column) => !column.isFixed),
    ...added.filter((column) => !column.isFixed)
  ]
}
